/**
 * Fonte única das chaves de permissão de Role no frontend (espelha
 * backend/src/constants/permissions.ts — sem monorepo/workspace compartilhado entre os projetos,
 * então não há como importar o arquivo do backend diretamente aqui; ambos precisam ser mantidos
 * em sincronia manualmente).
 *
 * Antes desta constante existir, a mesma lista de 13 chaves e a checagem legada de "é o admin
 * principal" estavam repetidas 4 vezes dentro de AuthContext.tsx (login, verify2FA, verifyToken,
 * refreshUserData) — e 3 dessas 4 cópias já haviam divergido umas das outras (faltava `finance`
 * em três delas, e `suggestions`/`providers` também em uma quarta), cada uma tratando o mesmo
 * usuário como admin ou não-admin de forma inconsistente dependendo de qual fluxo de login foi
 * usado para autenticar.
 */
export const PERMISSION_KEYS = [
  // Módulos Gerais (menu)
  'demands',
  'reports',
  'teamPerformance',
  'checklistCompliance',
  'suggestions',
  'providers',
  'clients',
  'users',
  'roles',
  'settings',
  'finance',
  'checklists',
  'patrimony',
  'qrScanner',
  // Configurações (abas)
  'configGeneral',
  'configDemands',
  'configReports',
  'configEmail',
] as const;

export type PermissionKey = typeof PERMISSION_KEYS[number];

export type PermissionsMap = Record<PermissionKey, boolean>;

/** Todas as permissões concedidas (ex.: usuário system admin, que não tem role de tenant). */
export const ALL_PERMISSIONS_GRANTED: PermissionsMap = Object.fromEntries(
  PERMISSION_KEYS.map((key) => [key, true])
) as PermissionsMap;

/** Constrói um PermissionsMap completo a partir de uma entrada parcial/não confiável (ex.: role.permissions vindo da API), com `false` como padrão para chaves ausentes. */
export const sanitizePermissions = (input: Partial<Record<string, unknown>> | null | undefined): PermissionsMap => {
  const result = {} as PermissionsMap;
  for (const key of PERMISSION_KEYS) {
    result[key] = input?.[key] === true;
  }
  return result;
};

/**
 * Determina se o usuário deve ser tratado como administrador principal. Prioriza a flag
 * explícita `isAdmin` da role; o fallback por permissões existe só para roles legadas criadas
 * antes dessa flag existir (precisa das 13 permissões, não de um subconjunto).
 */
export const isAdminRoleFromPermissions = (
  roleIsAdmin: boolean | undefined,
  rolePermissions: Partial<Record<string, unknown>> | null | undefined
): boolean => {
  if (roleIsAdmin === true) return true;
  if (!rolePermissions) return false;
  return PERMISSION_KEYS.every((key) => rolePermissions[key] === true);
};
